import { useCallback, useMemo } from 'react';
import useUserData from 'data-hooks/useUserData';
import { useMutation, useQuery } from '@apollo/client';

import { GET_FAVORITED_PRODUCT_IDS } from 'mocks/queries';
import usePdpId from 'components/consumer/PDP/state/context/usePdpId';
import useSetFavoriteModalState from 'global-state/favoriteModalState/useSetFavoriteModalState';
import { FAVORITE_PRODUCT_V2 } from 'mocks/mutations';

const useProductFavoriteV2 = () => {
  const productId = usePdpId();
  const { isLoggedIn } = useUserData();
  const { data } = useQuery(GET_FAVORITED_PRODUCT_IDS);
  const productIds = data?.productIds ?? [];
  const isFavorited = productIds.includes(productId);

  const setFavoriteModalState = useSetFavoriteModalState();

  const toggleFavoriteMutationOptions = useMemo(
    () => ({
      optimisticResponse: {
        userFavoriteActionV2: {
          id: productId,
          status: !isFavorited,
          __typename: 'FavResponseV2',
        },
      },
      update: (cache, { data: { userFavoriteActionV2 } }) => {
        const { productIds: favProductIds } = cache.readQuery({
          query: GET_FAVORITED_PRODUCT_IDS,
        });

        const newFavoritedProductIds = userFavoriteActionV2?.status
          ? [...favProductIds, userFavoriteActionV2?.id]
          : favProductIds.filter(id => id !== userFavoriteActionV2?.id);

        cache.writeQuery({
          query: GET_FAVORITED_PRODUCT_IDS,
          data: { productIds: newFavoritedProductIds },
        });
      },
      refetchQueries: [{ query: GET_FAVORITED_PRODUCT_IDS }],
    }),
    [isFavorited, productId]
  );

  const [toggleFavorite] = useMutation(
    FAVORITE_PRODUCT_V2,
    toggleFavoriteMutationOptions
  );

  const onFavClick = useCallback(
    event => {
      event.stopPropagation();
      event.preventDefault();

      if (!productId) {
        return;
      }

      if (!isLoggedIn) {
        setFavoriteModalState({ productId });
      } else {
        toggleFavorite({ variables: { productID: productId } });
      }
    },
    [productId, isLoggedIn, setFavoriteModalState, toggleFavorite]
  );

  return {
    isFavorited,
    onFavClick,
  };
};

export default useProductFavoriteV2;
